import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useDataAccumulatorForSingleOrder } from "../../../../context/dataAccumulatorForSingleOrder";
import { CopyIcon } from "../../../../svg/copy-icon";
import {
  AuthInfoField,
  buildSubmissionAuthInfoFields,
} from "../../utils/buildSubmissionAuthInfo";

/**
 * Renders a single label/value cell in the Auth Information grid, with an
 * optional copy-to-clipboard affordance.
 *
 * @input field - The label, value, and copyable flag for the cell.
 * @returns The grid cell element.
 */
const AuthInfoFieldCell = ({ field }: { field: AuthInfoField }) => {
  const handleCopy = () => {
    navigator.clipboard
      ?.writeText(field.value)
      .then(() => toast.success("Copied to clipboard"))
      .catch(() => toast.error("Couldn't copy to clipboard"));
  };

  return (
    <div className="submission-auth-info__field flex h-10 flex-1 flex-col items-start justify-between">
      <div className="text-tiny font-bold leading-tiny text-primaryGray-3">
        {field.label}
      </div>
      <div className="flex items-center gap-1.5 text-tiny font-normal leading-tiny tracking-tightest text-primaryGray-1">
        <span>{field.value}</span>
        {field.copyable && field.value !== "--" && (
          <CopyIcon
            className="hover:cursor-pointer"
            onClick={handleCopy}
            height="14"
            width="14"
          />
        )}
      </div>
    </div>
  );
};

/**
 * Auth Information section of the submission status screen: a bordered card
 * containing a 3-column grid of order details plus a comments block.
 *
 * @returns The Auth Information section element.
 */
const AuthInfoComponent = () => {
  const { generalOrderDetails, coverageDetails, authOnFileDetails } =
    useDataAccumulatorForSingleOrder();

  const primaryCoverage = coverageDetails?.coverages?.find(
    (coverage) => coverage.coverage_type === "primary",
  );

  const fields = buildSubmissionAuthInfoFields(
    generalOrderDetails?.order,
    primaryCoverage,
    authOnFileDetails?.auth_on_file,
  );

  return (
    <div className="submission-auth-info--container flex flex-col gap-4">
      <div className="submission-auth-info__heading text-small font-bold leading-small text-primaryGray-1">
        Auth Information
      </div>

      <div className="submission-auth-info--grid grid grid-cols-3 gap-x-6 gap-y-4">
        {fields.map((field) => (
          <AuthInfoFieldCell key={field.label} field={field} />
        ))}
      </div>
      {/* 
      <div className="submission-auth-info--comments flex flex-col gap-1">
        <div className="text-tiny font-bold leading-tiny text-primaryGray-3">
          Comments
        </div>
        <div className="self-stretch text-tiny font-normal leading-tiny tracking-tightest text-primaryGray-1">
          {comments}
        </div>
      </div> */}
    </div>
  );
};

export default AuthInfoComponent;
